import React, { useMemo, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CloseIcon from "@material-ui/icons/Close";

/*
 * Guia do CRM aberto pelo botão "Ajuda" (ao lado de Sair).
 * O conteúdo fica todo aqui, em texto: para atualizar o guia, edite a lista SECOES.
 * Cada item: { id, titulo, blocos: [{ subtitulo?, texto?, itens?: [] }] }.
 */
const SECOES = [
    {
        id: "comecar",
        titulo: "Primeiros passos",
        blocos: [
            {
                texto: "O CRM Prospect Brasil organiza a prospecção para os clientes: os clientes têm mailings (listas de empresas), os leads de cada mailing são trabalhados pelos assessores no board e, quando o decisor aceita, vira uma reunião agendada, que passa por auditoria, confirmação e briefing até ser realizada.",
            },
            {
                subtitulo: "Fluxo do trabalho",
                itens: [
                    "1. Cliente/projeto cadastrado em Clientes.",
                    "2. Mailing criado e leads importados (Mailing ou Leads → Importação).",
                    "3. Assessor trabalha os leads no board da Gestão do Assessor e registra cada contato com follow-up.",
                    "4. Lead aceitou: Agendar reunião (no painel do lead ou em Reuniões agendadas → Nova reunião).",
                    "5. Auditoria aprova ou reprova a reunião.",
                    "6. Confirmação acompanha o status (Confirmada, Reagendada, Realizada...) e o briefing é enviado.",
                    "7. Relatório do cliente reúne as reuniões do período.",
                ],
            },
            {
                subtitulo: "Menu e barra superior",
                itens: [
                    "O menu da esquerda mostra só as telas que o seu perfil pode abrir.",
                    "Busca rápida: Ctrl+K (ou clique em Buscar na barra superior) e digite razão social, CNPJ ou nome do cliente/projeto; Enter abre o resultado.",
                    "Ajuda (este guia) e Sair ficam no rodapé do menu; o ícone ? da barra superior também abre o guia.",
                ],
            },
        ],
    },
    {
        id: "perfis",
        titulo: "Perfis e permissões",
        blocos: [
            {
                texto: "Cada usuário tem um perfil. O master pode somar ações extras a um usuário em Usuários → Editar (exceto gerenciar usuários).",
            },
            {
                itens: [
                    "Master: tudo, inclusive usuários, convites e perfis.",
                    "Gestor: tudo menos gerenciar usuários. Vê a Home na visão Gestão (todos os assessores).",
                    "Auditor: visualiza, audita reuniões e baixa briefing. Vê a visão Gestão.",
                    "Assessor: trabalha leads (board, cadastro, contatos) e agenda reuniões. Vê só os próprios números e leads.",
                    "Confirmação: visualiza, confirma reuniões e baixa briefing.",
                ],
            },
            {
                subtitulo: "Ações",
                itens: [
                    "Visualizar: ver clientes, mailings, leads e reuniões.",
                    "Editar leads: criar e editar leads, mover no board, registrar contatos.",
                    "Editar reuniões: agendar e editar reuniões.",
                    "Confirmar: data da confirmação, status e observações da reunião.",
                    "Auditar: aprovar ou reprovar a auditoria e escrever a observação.",
                    "Editar clientes: criar e editar clientes e mailings.",
                    "Excluir: excluir leads, clientes, mailings e reuniões.",
                    "Exportar / Importar: planilhas de leads e agendas.",
                    "Gerar relatórios: Relatório do cliente.",
                    "Visão Gestão: Home com filtro por assessor.",
                    "Baixar briefing: só depois da auditoria aprovada.",
                ],
            },
        ],
    },
    {
        id: "inicio",
        titulo: "Início (painel)",
        blocos: [
            {
                texto: "Painel com indicadores do período (padrão: mês atual). Filtre por período, projeto (cliente) e, na visão Gestão, por assessor. Quem não tem a visão Gestão vê sempre os próprios números.",
            },
            {
                subtitulo: "Personalizar",
                itens: ["Use Personalizar para escolher, ordenar e redimensionar os módulos. O layout fica salvo para o seu usuário."],
            },
            {
                subtitulo: "Principais indicadores",
                itens: [
                    "Reuniões realizadas: status Realizada, contadas pela data da reunião. É o indicador principal.",
                    "Reuniões agendadas: contadas pela data em que o assessor agendou (veja Regras de contagem).",
                    "Taxa de realização: realizadas ÷ reuniões com data no período até hoje.",
                    "Aguardando auditoria: reuniões com auditoria Aguardando; atrasadas = criadas há mais de 48 h.",
                    "Briefings pendentes: reuniões futuras sem briefing enviado; urgentes = nos próximos 2 dias.",
                    "Follow-ups de hoje / atrasados: pelo último registro de contato de cada lead no board.",
                    "Situação das agendadas: distribuição por status de TODAS as reuniões agendadas no período, inclusive declinadas e não aprovadas (por isso a soma pode passar do card Reuniões agendadas).",
                ],
            },
        ],
    },
    {
        id: "meus-leads",
        titulo: "Meus leads de hoje",
        blocos: [
            {
                texto: "Lista única dos seus follow-ups vencidos e de hoje, de todos os mailings, do mais antigo para o mais novo.",
            },
            {
                itens: [
                    "Filtre por Cliente/projeto ou marque Só atrasados.",
                    "Contatos: abre telefone da empresa e decisores com ligar, WhatsApp, e-mail, LinkedIn e copiar.",
                    "Clique na empresa para abrir o cadastro do lead e registrar o contato com o próximo follow-up.",
                ],
            },
        ],
    },
    {
        id: "board",
        titulo: "Gestão do Assessor (board)",
        blocos: [
            {
                texto: "Quadro dos leads por situação. Para mudar a situação, arraste o card entre as colunas, use o botão Mover do card ou o campo Situação no painel lateral (precisa da ação Editar leads). Ao mover pelo botão, o card vai para o topo da coluna escolhida.",
            },
            {
                subtitulo: "Filtros",
                itens: [
                    "Razão social, Cliente/projeto, Mailing e Assessor; em Mais filtros: Estado, Cidade e Follow-up.",
                    "Ao escolher o mailing o board já carrega. O CRM lembra o último cliente + mailing usado; limpar desfaz.",
                    "Gestor e master filtram por qualquer assessor; o assessor filtra só pelos próprios leads.",
                    "As listas de Cliente/projeto e Assessor dos filtros mostram só os ativos.",
                    "Os filtros ativos aparecem como etiquetas; o X de cada etiqueta remove só aquele filtro.",
                ],
            },
            {
                subtitulo: "Colunas",
                itens: [
                    "Em Branco, Em andamento, Retorno futuro, Aguardando retorno, Sem interesse, Sem perfil, Black list e Reunião agendada.",
                    "Sem perfil e Black list começam recolhidas; use mostrar para abrir. O ícone de olho recolhe uma coluna.",
                ],
            },
            {
                subtitulo: "Card e painel lateral",
                itens: [
                    "Selo do follow-up: vermelho = atrasado, amarelo = em até 7 dias, verde = depois de 7 dias.",
                    "Bolinha com as iniciais = assessor do lead.",
                    "Contatos: telefones e decisores com ações de um clique.",
                    "Clique no nome da empresa para abrir o painel: assessor (pode trocar), mailing, decisor, contato, último contato, Agendar reunião e Registrar contato / editar cadastro.",
                    "Board / Lista: a lista mostra os mesmos leads em tabela.",
                ],
            },
        ],
    },
    {
        id: "leads",
        titulo: "Leads e cadastro do lead",
        blocos: [
            {
                itens: [
                    "Leads: lista com busca e filtros; Novo lead cria um cadastro avulso.",
                    "Importação: envie a planilha no modelo; o CRM mostra os erros por linha em Importações recentes.",
                    "O cadastro do lead é dividido em abas: Empresa, Decisores, Contatos e resumos e Reuniões.",
                    "Decisores: nome, cargo, e-mail, telefone e LinkedIn; a coluna Contato liga, abre WhatsApp, e-mail ou LinkedIn.",
                    "Registrar contato: escreva o resumo, a data, o próximo follow-up e o decisor. O follow-up alimenta o board, a Home e Meus leads de hoje.",
                    "Histórico de agendamentos: reuniões já marcadas para o lead.",
                ],
            },
        ],
    },
    {
        id: "clientes",
        titulo: "Clientes e Mailings",
        blocos: [
            {
                itens: [
                    "Clientes: cadastro do cliente/projeto, contatos (executivos que recebem as reuniões) e mailings vinculados.",
                    "Mailing: lista de leads trabalhada para um ou mais clientes. Dentro do mailing dá para incluir leads e importar planilha.",
                    "Criar e editar exige a ação Editar clientes; excluir exige Excluir.",
                ],
            },
        ],
    },
    {
        id: "reunioes",
        titulo: "Reuniões agendadas",
        blocos: [
            {
                subtitulo: "Lista e calendário",
                itens: [
                    "Filtros: Reunião de/até, Agendamento de/até, Cliente/projeto, Assessor, Tipo (Reunião ou Oportunidade), Status Auditoria, Status Confirmação e Status Briefing.",
                    "Pesquisar aplica os filtros e volta para a página 1; Limpar tira todos.",
                    "Calendário: visões Dia, Semana e Mês com os mesmos filtros; a cor segue o status da confirmação.",
                    "Exportar baixa a planilha de agendas (ação Exportar).",
                    "Nº da reunião, Data Reunião (sempre que preenchida) e Hora no formato HH:MM; o calendário também mostra o Nº.",
                ],
            },
            {
                subtitulo: "Página da reunião",
                itens: [
                    "Dados: cliente, assessor, contato do lead, executivo, tipo, data, hora, formato e resumo.",
                    "Oportunidade exige a data da oportunidade e não tem hora nem formato.",
                    "Auditoria (ação Auditar): Aprovada, Aguardando ou Não aprovada, com observação.",
                    "Confirmação: a equipe de confirmação muda status, data da confirmação e observações. Quem agenda (assessor) também muda o status e a data da confirmação, mas não marca Realizada nem Não aprovada, e não altera reuniões que já estão nesses status. Para Reagendada informe a nova data.",
                    "Mudou a data de uma reunião que já tinha data? O status passa sozinho para Reagendada.",
                    "Data da confirmação: preenchida sozinha com 2 dias úteis antes da reunião (sábados e domingos não contam; feriados não entram na conta). Pode ser editada.",
                    "Briefing: baixe o PDF depois da auditoria aprovada.",
                    "Histórico de modificações (fim da página): quem alterou, o que mudou (antes → depois), dia e horário. Começou a registrar em 04/11/2026.",
                ],
            },
        ],
    },
    {
        id: "status",
        titulo: "Significado dos status",
        blocos: [
            {
                subtitulo: "Auditoria",
                itens: [
                    "Em branco: ainda não entrou na fila.",
                    "Aguardando: na fila da auditoria.",
                    "Aprovada: reunião válida; libera o briefing.",
                    "Não aprovada: reunião reprovada. A confirmação passa automaticamente para Não aprovada e a reunião sai das agendadas. Se a auditoria for revista, a confirmação volta para Aguardando.",
                ],
            },
            {
                subtitulo: "Confirmação",
                itens: [
                    "Aguardando: ainda não confirmada com o lead.",
                    "Confirmada: lead confirmou data e hora.",
                    "Reagendada: mudou de data (a nova data é obrigatória). Aplicada sozinha quando a data da reunião é alterada.",
                    "Aguardando nova data: o lead vai indicar outra data; data e hora ficam em branco.",
                    "Realizada: a reunião aconteceu (conta como realizada pela data da reunião). Só a equipe de confirmação marca.",
                    "Declinada: o lead desistiu.",
                    "Não aprovada: reprovada (pela auditoria ou pela confirmação).",
                ],
            },
            {
                subtitulo: "Briefing",
                itens: ["Aguardando: briefing não enviado ao executivo.", "Enviado: briefing enviado."],
            },
        ],
    },
    {
        id: "contagem",
        titulo: "Regras de contagem",
        blocos: [
            {
                subtitulo: "Agendadas (painel e relatório)",
                itens: [
                    "Contam pela data em que o assessor agendou (Data Agendamento).",
                    "Não contam: reuniões com auditoria Não aprovada.",
                    "Não contam: reuniões com confirmação Não aprovada.",
                    "Não contam: reuniões declinadas no mesmo mês do agendamento. Declinada em um mês seguinte continua contando no mês em que foi agendada.",
                    "A data do declínio vem do histórico de modificações; para reuniões declinadas antes de 04/11/2026 vale a data da última alteração da confirmação.",
                ],
            },
            {
                subtitulo: "Realizadas",
                itens: ["Status Realizada, contadas pela data da reunião."],
            },
            {
                subtitulo: "Relatório do cliente",
                itens: [
                    "Reuniões do período pela data da reunião, com briefing enviado.",
                    "Declinadas e não aprovadas nunca entram no relatório do cliente, e as não aprovadas na auditoria também ficam de fora.",
                    "Previstas: reuniões agendadas no período com data depois do fim do período.",
                ],
            },
        ],
    },
    {
        id: "relatorio",
        titulo: "Relatório do cliente",
        blocos: [
            {
                itens: [
                    "Escolha o cliente, o período e um ou mais mailings (o campo Mailings aceita vários; trocar o cliente limpa a seleção).",
                    "Escolha o agrupamento por região (cidade, estado, país...).",
                    "O relatório traz reuniões do período, previstas, oportunidades, aguardando nova data, por status e por região.",
                ],
            },
        ],
    },
    {
        id: "usuarios",
        titulo: "Usuários, convites e Meu cadastro",
        blocos: [
            {
                itens: [
                    "Usuários (master): cria usuário, define perfil e ações extras, ativa/desativa e gera link de redefinição de senha.",
                    "Convites: link para a pessoa criar o próprio acesso; o link pode ser enviado por e-mail ou copiado.",
                    "Meu cadastro: cada usuário edita nome e telefone e troca a própria senha.",
                    "Esqueceu a senha: peça ao master um link de redefinição.",
                ],
            },
        ],
    },
    {
        id: "duvidas",
        titulo: "Dúvidas frequentes",
        blocos: [
            {
                itens: [
                    "Não vejo uma tela no menu: o seu perfil não tem a ação necessária. Fale com o master.",
                    "O botão do briefing não aparece: a auditoria ainda não está Aprovada.",
                    "O card Reuniões agendadas é menor que a Situação das agendadas: a Situação mostra todas, inclusive as que não contam (não aprovadas e declinadas no mês).",
                    "O assessor não vê os leads de outro assessor: é proposital; gestor e master veem todos.",
                    "Data inválida: use o ano com 4 dígitos, entre 2000 e 2100.",
                    "Mudei algo e quero saber quem alterou a reunião: veja o Histórico de modificações no fim da página da reunião.",
                ],
            },
        ],
    },
];

const textoDaSecao = (s) => [
    s.titulo,
    ...s.blocos.flatMap((b) => [b.subtitulo || "", b.texto || "", ...(b.itens || [])]),
].join(" ").toLowerCase();

const AjudaCRM = ({ aberto, onFechar }) => {
    const telaPequena = useMediaQuery("(max-width:700px)");
    const [busca, setBusca] = useState("");
    const termo = busca.trim().toLowerCase();

    const visiveis = useMemo(
        () => (termo ? SECOES.filter((s) => textoDaSecao(s).includes(termo)) : SECOES),
        [termo]
    );

    const irPara = (id) => {
        const el = document.getElementById(`ajuda-${id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <Dialog open={aberto} onClose={onFechar} fullScreen={telaPequena} maxWidth="lg" fullWidth aria-labelledby="ajuda-titulo">
            <DialogTitle id="ajuda-titulo" disableTypography style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 8 }}>
                <span style={{ fontSize: 20, fontWeight: 700 }}>Ajuda do CRM</span>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Buscar no guia"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    style={{ marginLeft: "auto", minWidth: 180 }}
                    inputProps={{ "aria-label": "Buscar no guia" }}
                />
                <IconButton size="small" onClick={onFechar} aria-label="Fechar ajuda">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers style={{ display: "flex", gap: 24, padding: 0 }}>
                {!telaPequena && (
                    <nav aria-label="Seções da ajuda" style={{ flex: "none", width: 220, borderRight: "1px solid #E0E4DC", padding: "12px 0", position: "sticky", top: 0, alignSelf: "flex-start" }}>
                        {visiveis.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => irPara(s.id)}
                                style={{ all: "unset", display: "block", cursor: "pointer", padding: "6px 16px", fontSize: 14, color: "#2F6420" }}
                            >
                                {s.titulo}
                            </button>
                        ))}
                    </nav>
                )}
                <div style={{ flex: 1, minWidth: 0, padding: "12px 20px 24px", fontSize: 14, lineHeight: 1.55 }}>
                    {visiveis.length === 0 && <p>Nada encontrado para "{busca}".</p>}
                    {visiveis.map((s) => (
                        <section key={s.id} id={`ajuda-${s.id}`} style={{ marginBottom: 24, scrollMarginTop: 8 }}>
                            <h2 style={{ fontSize: 17, margin: "8px 0", color: "#1F4615" }}>{s.titulo}</h2>
                            {s.blocos.map((b, i) => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    {b.subtitulo && <h3 style={{ fontSize: 15, margin: "10px 0 4px" }}>{b.subtitulo}</h3>}
                                    {b.texto && <p style={{ margin: "4px 0" }}>{b.texto}</p>}
                                    {b.itens && (
                                        <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                                            {b.itens.map((item, j) => <li key={j}>{item}</li>)}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </section>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AjudaCRM;
